import { Header } from "@/components/Header";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  TrendingUp, 
  Target, 
  DollarSign, 
  Users, 
  BarChart3, 
  Lightbulb,
  CheckCircle,
  AlertTriangle,
  Calculator,
  PieChart,
  Briefcase,
  Building,
  Package
} from "lucide-react";


const staticRecommendations = [
  {
    id: 1,
    icon: TrendingUp,
    title: "Análise de Fluxo de Caixa",
    description: "Monitore regularmente seu fluxo de caixa para identificar padrões sazonais e gargalos financeiros. Um fluxo de caixa bem gerenciado é fundamental para a saúde do negócio.",
    category: "Financeiro",
    priority: "Alta",
    action: "Criar relatórios mensais de fluxo de caixa",
    details: [
      "Implemente um controle diário de entradas e saídas",
      "Identifique períodos de maior necessidade de capital de giro",
      "Negocie prazos de pagamento com fornecedores",
      "Estabeleça reservas de emergência (3-6 meses de despesas fixas)"
    ],
    benefits: "Evita surpresas financeiras e permite melhor planejamento estratégico"
  },
  {
    id: 2,
    icon: Target,
    title: "Redução de Custos Operacionais",
    description: "Identifique despesas desnecessárias e oportunidades de economia sem comprometer a qualidade do produto ou serviço oferecido.",
    category: "Operacional",
    priority: "Alta",
    action: "Revisar contratos e buscar alternativas mais econômicas",
    details: [
      "Renegocie contratos de fornecedores anualmente",
      "Avalie gastos com energia, telefonia e internet",
      "Considere terceirização de serviços não-core",
      "Implemente políticas de controle de gastos"
    ],
    benefits: "Aumento da margem de lucro e melhoria da competitividade"
  },
  {
    id: 3,
    icon: DollarSign,
    title: "Diversificação de Receitas",
    description: "Explore novas fontes de receita para reduzir dependência de poucos clientes ou produtos, criando um negócio mais resiliente e sustentável.",
    category: "Estratégico",
    priority: "Alta",
    action: "Pesquisar mercados adjacentes ou novos produtos/serviços",
    details: [
      "Analise produtos/serviços complementares ao seu negócio",
      "Explore diferentes canais de vendas (online, parcerias)",
      "Considere modelos de receita recorrente (assinaturas, contratos)",
      "Desenvolva produtos/serviços premium"
    ],
    benefits: "Reduz riscos e aumenta o potencial de crescimento"
  },
  {
    id: 4,
    icon: Users,
    title: "Estratégias de Fidelização",
    description: "Implemente programas estruturados de fidelização para aumentar a retenção de clientes e o ticket médio, maximizando o valor do ciclo de vida do cliente.",
    category: "Marketing",
    priority: "Média",
    action: "Criar programa de pontos ou descontos para clientes recorrentes",
    details: [
      "Desenvolva um programa de pontos ou cashback",
      "Ofereça descontos progressivos por volume de compras",
      "Crie comunicação personalizada por segmento de cliente",
      "Implemente pesquisas de satisfação regulares"
    ],
    benefits: "Aumenta lifetime value e reduz custo de aquisição de novos clientes"
  },
  {
    id: 5,
    icon: BarChart3,
    title: "Análise de Rentabilidade por Produto",
    description: "Identifique quais produtos ou serviços são mais lucrativos para focar recursos e energia nos segmentos mais rentáveis do seu negócio.",
    category: "Financeiro",
    priority: "Alta",
    action: "Calcular margem de contribuição por produto/serviço",
    details: [
      "Calcule custos diretos e indiretos de cada produto/serviço",
      "Analise margem de contribuição individual",
      "Avalie produtos com baixa rentabilidade",
      "Considere aumentar preços ou descontinuar produtos não lucrativos"
    ],
    benefits: "Otimiza mix de produtos e maximiza lucratividade geral"
  },
  {
    id: 6,
    icon: Calculator,
    title: "Controle de Inadimplência",
    description: "Implemente processos eficientes de cobrança e análise de crédito para reduzir perdas com clientes inadimplentes.",
    category: "Financeiro",
    priority: "Média",
    action: "Criar processo estruturado de análise de crédito e cobrança",
    details: [
      "Estabeleça critérios claros para concessão de crédito",
      "Implemente cobrança automatizada por e-mail/SMS",
      "Ofereça facilidades de pagamento para negociação",
      "Monitore indicadores de inadimplência mensalmente"
    ],
    benefits: "Reduz perdas financeiras e melhora fluxo de caixa"
  },
  {
    id: 7,
    icon: PieChart,
    title: "Análise de Concorrência",
    description: "Monitore regularmente seus concorrentes para identificar oportunidades de diferenciação e melhoria no seu posicionamento de mercado.",
    category: "Estratégico",
    priority: "Média",
    action: "Realizar pesquisa trimestral de concorrentes",
    details: [
      "Monitore preços praticados pela concorrência",
      "Analise estratégias de marketing dos concorrentes",
      "Identifique gaps no mercado não atendidos",
      "Avalie pontos fortes e fracos relativos"
    ],
    benefits: "Melhora posicionamento competitivo e identifica oportunidades"
  },
  {
    id: 8,
    icon: Briefcase,
    title: "Capacitação da Equipe",
    description: "Invista no desenvolvimento da sua equipe para aumentar produtividade, qualidade do serviço e satisfação dos colaboradores.",
    category: "Recursos Humanos",
    priority: "Média",
    action: "Criar plano de treinamento e desenvolvimento",
    details: [
      "Identifique gaps de competência na equipe",
      "Ofereça treinamentos técnicos e comportamentais",
      "Implemente plano de carreira estruturado",
      "Reconheça e recompense bons desempenhos"
    ],
    benefits: "Aumenta produtividade e reduz turnover de funcionários"
  }
];

const Recomendacoes = () => {

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'Alta': return 'bg-destructive text-destructive-foreground';
      case 'Média': return 'bg-warning text-warning-foreground';
      case 'Baixa': return 'bg-success text-success-foreground';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="page-bleed py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Recomendações para Seu Negócio</h1>
          <p className="text-muted-foreground">
            Insights e sugestões baseadas em análises financeiras para otimizar sua empresa
          </p>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lightbulb className="h-5 w-5 text-primary" />
                Recomendações para Melhorar Seu Negócio
              </CardTitle>
              <CardDescription>
                Estratégias comprovadas para otimizar performance e aumentar lucratividade
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {staticRecommendations.map((rec) => {
                  const IconComponent = rec.icon;
                  return (
                    <Card key={rec.id} className="p-6 hover:shadow-lg transition-shadow">
                      <div className="space-y-4">
                        <div className="flex items-start gap-3">
                          <div className="p-3 bg-primary/10 rounded-lg">
                            <IconComponent className="h-6 w-6 text-primary" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-2">
                              <h3 className="font-semibold text-lg">{rec.title}</h3>
                              <Badge className={getPriorityColor(rec.priority)}>
                                {rec.priority}
                              </Badge>
                            </div>
                            <Badge variant="outline" className="mb-3">{rec.category}</Badge>
                          </div>
                        </div>
                        
                        <p className="text-muted-foreground leading-relaxed">
                          {rec.description}
                        </p>

                        <div className="space-y-3">
                          <div className="bg-primary/5 p-3 rounded-lg">
                            <h4 className="font-medium text-sm mb-2 flex items-center gap-1">
                              <CheckCircle className="h-4 w-4 text-primary" />
                              Ação Imediata
                            </h4>
                            <p className="text-sm">{rec.action}</p>
                          </div>

                          <div className="space-y-2">
                            <h4 className="font-medium text-sm flex items-center gap-1">
                              <AlertTriangle className="h-4 w-4 text-warning" />
                              Passos Detalhados
                            </h4>
                            <ul className="space-y-1">
                              {rec.details.map((detail, index) => (
                                <li key={index} className="text-sm text-muted-foreground flex items-start gap-2">
                                  <span className="text-primary font-semibold mt-1">•</span>
                                  {detail}
                                </li>
                              ))}
                            </ul>
                          </div>

                          <div className="bg-success/5 p-3 rounded-lg border border-success/20">
                            <h4 className="font-medium text-sm mb-1 text-success flex items-center gap-1">
                              <Building className="h-4 w-4" />
                              Benefício Esperado
                            </h4>
                            <p className="text-sm text-success/80">{rec.benefits}</p>
                          </div>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default Recomendacoes;